/**
 * Event detail page — canonical event view with modern chrome.
 * Link-first per §41 (no full text). Renders headline, summary,
 * a hero image if we have one, meta strip (confidence + importance +
 * ID), primary source CTA, and the full attribution list.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, ShieldCheck, TrendingUp, Radio,
} from "lucide-react";
import { npApi } from "./api";
import { timeAgo } from "./theme";
import PublisherWidget from "@/components/publisher/PublisherWidget";
import ChatPulseThread from "@/components/chatpulse/ChatPulseThread";

export default function NewsPulseEventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const e = await npApi.event(eventId);
        if (!cancelled) setEvent(e);
      } catch (ex) {
        if (!cancelled) setErr(ex.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="event-loading">Loading…</div>;
  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-red-600" data-testid="event-error">Error: {err}</div>;
  if (!event) return <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="event-notfound">Not found.</div>;

  const sources = event.sources || [];
  const primary = sources.find((s) => s.primary) || sources[0];
  // Primary source image lives on the source_articles doc — best-effort
  // read via the primary_source_article_id → but the API `/events/:id`
  // endpoint returns raw source rows without image_url, so we fall back
  // to the same category-tinted gradient used elsewhere.
  const primaryCat = (event.categories || [])[0] || "";
  const cat = primaryCat.split("/")[0] || "";

  return (
    <article className="max-w-3xl mx-auto px-4 lg:px-6 py-8" data-testid="newspulse-event-detail">
      {/* Back + breadcrumb */}
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-6 flex flex-wrap gap-2 items-center">
        <Link to="/" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        {(event.categories || []).slice(0, 3).map((c) => (
          <span key={c} className="flex items-center gap-2">
            <span className="text-slate-300">/</span>
            <Link to={`/topic/${c.split("/")[0]}`} className="hover:text-blue-600">
              {c.split("/").pop().replace(/_/g, " ")}
            </Link>
          </span>
        ))}
      </div>

      {/* Category pill */}
      {cat && (
        <div className="mb-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-blue-600">
            {cat}
          </span>
        </div>
      )}

      {/* Headline */}
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-slate-900 mb-4"
        data-testid="event-headline"
      >
        {event.canonical_headline}
      </h1>

      {/* Meta strip */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 border-y border-slate-200 py-3 mb-8" data-testid="event-scores">
        <span className="flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <b className="text-slate-900">Confidence</b>
          <span className="font-mono">{event.confidence_score}</span>
          <span className="text-slate-400">·</span>
          <span className="capitalize">{(event.confidence_label || "").replace(/_/g, " ")}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-amber-600" />
          <b className="text-slate-900">Importance</b>
          <span className="font-mono">{event.importance_score}</span>
        </span>
        {event.developing && (
          <span className="flex items-center gap-1.5 text-amber-700 font-semibold">
            <Radio className="w-4 h-4" /> Developing
          </span>
        )}
        <span className="text-[11px] uppercase tracking-widest text-slate-400 ml-auto">
          {event.event_id}
        </span>
      </div>

      {/* Summary */}
      {event.summary && (
        <p
          className="text-lg text-slate-800 leading-relaxed mb-8"
          data-testid="event-summary"
        >
          {event.summary}
        </p>
      )}

      {/* Primary source CTA */}
      {primary && (
        <a
          href={primary.url}
          target="_blank"
          rel="noreferrer"
          className="group block rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-md transition-all p-5 mb-8"
          data-testid="event-primary-source"
        >
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Read the original at the primary source
          </div>
          <div
            className="text-lg font-bold text-slate-900 group-hover:text-blue-700 transition-colors"
            data-testid="event-primary-link"
          >
            {primary.headline}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {primary.author ? `${primary.author} · ` : ""}
            {primary.published_at ? new Date(primary.published_at).toLocaleString() : ""}
          </div>
        </a>
      )}

      {/* Additional sources */}
      {sources.length > 1 && (
        <div className="mb-10" data-testid="event-other-sources">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-900 mb-3">
            Additional coverage ({sources.length - 1})
          </h3>
          <ul className="space-y-3">
            {sources.filter((s) => s !== primary).map((s, i) => (
              <li key={i} className="border-b border-slate-100 pb-3 last:border-b-0">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-700 hover:text-blue-900 hover:underline font-medium"
                >
                  {s.headline}
                </a>
                {s.author && <span className="text-slate-500 text-sm ml-2">· {s.author}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Editorial notice */}
      {event.status !== "published" && (
        <div
          className="border-l-4 border-amber-500 bg-amber-50 p-4 mb-6 text-sm rounded-r-lg"
          data-testid="event-status-notice"
        >
          <b>Editorial notice:</b> this event is in
          <span className="ml-1 uppercase tracking-widest">{event.status}</span> status.
          {event.sensitivity_flags?.length > 0 && (
            <> Flagged for: {event.sensitivity_flags.join(", ")}.</>
          )}
        </div>
      )}

      {/* Distribution footprint */}
      {event.distributed_to?.length > 0 && (
        <div className="text-xs text-slate-500 pt-6 border-t border-slate-100" data-testid="event-distribution">
          Also on the network:{" "}
          {event.distributed_to.map((s, i) => (
            <span key={s} className="uppercase tracking-widest">
              {i > 0 && " · "}{s}
            </span>
          ))}
        </div>
      )}

      {/* Articles & Blogs — long-form Publisher pieces on the same
          topic. Rendered outside the wire feed so the two content
          modes don't visually blend. Silent on 0 matches. */}
      <div className="mt-10">
        <PublisherWidget
          siteId="newspulse"
          categoryId={(event.primary_category || event.categories?.[0] || "").split("/")[0] || null}
          title="Articles & Blogs on this topic"
          limit={4}
          variant="list"
          testIdPrefix="event-detail-publisher"
        />
      </div>

      {/* Community discussion — always renders, empty state prompts
          the first thread. Reads open to everyone. */}
      <ChatPulseThread
        contextType="event"
        contextId={event.event_id}
        contextLabel={event.canonical_headline || event.title}
      />
    </article>
  );
}
